// Math module — per PRD §8, ported verbatim from broker-team-analyzer-preview.html.
// Pure functions. No React. No side effects.

export function computeAnnualResults(slot, deals) {
  let brokerageCapBalance = Math.max(
    0,
    (slot.annual_cap || 0) - (slot.already_paid_to_cap || 0),
  );
  let royaltyCapBalance = slot.is_kw ? (slot.kw_royalty_cap || 3000) : 0;
  let teamCapBalance =
    slot.on_team && slot.team_cap ? slot.team_cap : Infinity;

  let capHitMonth = null;
  let totalGCI = 0;
  let totalReferralOut = 0;
  let totalConcessions = 0;
  let totalRoyalty = 0;
  let totalBrokerageTake = 0;
  let totalTeamTake = 0;
  let totalTcFees = 0;
  let totalNetFromDeals = 0;
  const perDeal = [];

  const sorted = [...deals].sort(
    (a, b) => (a.close_month ?? 0) - (b.close_month ?? 0),
  );

  for (const deal of sorted) {
    const salePrice = Number(deal.sale_price) || 0;
    const commPct = Number(deal.commission_pct) || 0;
    const gci = salePrice * (commPct / 100);

    const referralPct = Number(deal.referral_out_pct) || 0;
    const referralOut = gci * (referralPct / 100);
    const concessions = Number(deal.concessions) || 0;

    let royalty = 0;
    if (slot.is_kw && royaltyCapBalance > 0) {
      const rawRoy = gci * 0.06;
      royalty = Math.min(rawRoy, royaltyCapBalance);
      royaltyCapBalance -= royalty;
    }

    let pool = gci - referralOut - concessions - royalty;
    if (pool < 0) pool = 0;

    let teamTake = 0;
    let brokerageTake = 0;
    const waterfall = slot.waterfall || 'team_first';

    const applyBrokerage = (base) => {
      if (slot.annual_cap === 0 || !slot.annual_cap) {
        return Number(slot.post_cap_fee) || 0;
      }
      if (brokerageCapBalance > 0) {
        const brokerageShare = 1 - Number(slot.agent_split_pct) / 100;
        let take = base * brokerageShare;
        if (take >= brokerageCapBalance) {
          take = brokerageCapBalance;
          brokerageCapBalance = 0;
          if (!capHitMonth) capHitMonth = deal.close_month;
        } else {
          brokerageCapBalance -= take;
        }
        return take;
      }
      return Number(slot.post_cap_fee) || 0;
    };

    if (slot.on_team && waterfall === 'team_first') {
      const teamPct = Number(slot.team_split_pct) / 100 || 0;
      let teamShare = pool * teamPct;
      if (teamShare > teamCapBalance) {
        teamShare = teamCapBalance;
      }
      teamCapBalance -= teamShare;
      teamTake = teamShare;

      const agentBase = pool - teamTake;
      brokerageTake = applyBrokerage(agentBase);
    } else if (slot.on_team && waterfall === 'brokerage_first') {
      brokerageTake = applyBrokerage(pool);
      const afterBrokerage = pool - brokerageTake;
      const teamPct = Number(slot.team_split_pct) / 100 || 0;
      let teamShare = afterBrokerage * teamPct;
      if (teamShare > teamCapBalance) {
        teamShare = teamCapBalance;
      }
      teamCapBalance -= teamShare;
      teamTake = teamShare;
    } else {
      brokerageTake = applyBrokerage(pool);
    }

    const tcFees = Number(deal.tc_fees) || 0;
    const teamPerDealFees = slot.on_team
      ? Number(slot.team_per_deal_fee) || 0
      : 0;
    const dealNet = pool - brokerageTake - teamTake - tcFees - teamPerDealFees;

    totalGCI += gci;
    totalReferralOut += referralOut;
    totalConcessions += concessions;
    totalRoyalty += royalty;
    totalBrokerageTake += brokerageTake;
    totalTeamTake += teamTake;
    totalTcFees += tcFees + teamPerDealFees;
    totalNetFromDeals += dealNet;

    perDeal.push({
      id: deal.id,
      gci,
      royalty,
      referralOut,
      concessions,
      teamTake,
      brokerageTake,
      tcFees: tcFees + teamPerDealFees,
      net: dealNet,
      cappedDeal:
        slot.annual_cap > 0 &&
        brokerageCapBalance === 0 &&
        capHitMonth !== null &&
        deal.close_month >= capHitMonth,
    });
  }

  const monthlyFees = (Number(slot.monthly_fees) || 0) * 12;
  const annualEo = Number(slot.annual_eo) || 0;
  const teamMonthlyFees = slot.on_team
    ? (Number(slot.team_monthly_fee) || 0) * 12
    : 0;

  const annualNet = totalNetFromDeals - monthlyFees - annualEo - teamMonthlyFees;
  const totalFeesPaid = totalGCI - annualNet;
  const effectiveSplit = totalGCI > 0 ? annualNet / totalGCI : 0;

  const prices = deals
    .map((d) => Number(d.sale_price) || 0)
    .filter((p) => p > 0)
    .sort((a, b) => a - b);
  const medianPrice = prices.length ? prices[Math.floor(prices.length / 2)] : 0;
  const medianCommPct = deals.length
    ? deals.reduce((s, d) => s + Number(d.commission_pct || 0), 0) / deals.length
    : 2.5;
  const repGCI = medianPrice * (medianCommPct / 100);
  const postCapFlatFee = Number(slot.post_cap_fee) || 0;
  const postCapNet =
    repGCI -
    postCapFlatFee -
    (slot.on_team ? (repGCI * (slot.team_split_pct || 0)) / 100 : 0);

  return {
    annualNet,
    totalGCI,
    totalFeesPaid,
    effectiveSplit,
    capHitMonth,
    postCapNet,
    perDeal,
    breakdown: {
      totalRoyalty,
      totalReferralOut,
      totalConcessions,
      totalBrokerageTake,
      totalTeamTake,
      totalTcFees,
      monthlyFees,
      annualEo,
      teamMonthlyFees,
      totalNetFromDeals,
    },
  };
}

export function fmtUSD(n, opts = {}) {
  const { sign = false, cents = false } = opts;
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const str =
    '$' +
    abs.toLocaleString('en-US', {
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0,
    });
  if (sign) return (n >= 0 ? '+' : '−') + str;
  return (n < 0 ? '−' : '') + str;
}

export function fmtPct(n, decimals = 1) {
  if (n == null || Number.isNaN(n)) return '—';
  return (n * 100).toFixed(decimals) + '%';
}
